/**
 * ApprovalTimeline — Visual vertical timeline of GL Journal workflow history.
 *
 * Consumes the workflowHistory array already embedded in GlJournalDto.
 * For a generic entity, use WorkflowStatusPanel instead.
 */

import { CheckCircle2, XCircle, Send, BookCheck, RotateCcw, Ban, Clock } from 'lucide-react';
import type { GlJournalWorkflowHistoryDto } from '@/types/glJournal.types';
import { cn } from '@/utils/cn';

interface ApprovalTimelineProps {
  history:    GlJournalWorkflowHistoryDto[];
  className?: string;
}

const actionIcon = (action: string, toStatus: string) => {
  if (toStatus === 'Posted')   return <BookCheck size={14} className="text-emerald-600" />;
  if (toStatus === 'Approved') return <CheckCircle2 size={14} className="text-green-600" />;
  if (toStatus === 'Rejected') return <XCircle size={14} className="text-red-600" />;
  if (action   === 'SUBMIT')   return <Send size={14} className="text-blue-600" />;
  if (action   === 'REVERSE')  return <RotateCcw size={14} className="text-amber-600" />;
  if (action   === 'CANCEL')   return <Ban size={14} className="text-rose-600" />;
  return <Clock size={14} className="text-slate-400" />;
};

const dotColor = (toStatus: string): string => {
  switch (toStatus) {
    case 'Posted':            return 'bg-emerald-500 ring-emerald-200 dark:ring-emerald-800';
    case 'Approved':          return 'bg-green-500 ring-green-200 dark:ring-green-800';
    case 'Rejected':          return 'bg-red-500 ring-red-200 dark:ring-red-800';
    case 'Submitted':
    case 'PendingApproval':   return 'bg-blue-500 ring-blue-200 dark:ring-blue-800';
    case 'Cancelled':         return 'bg-rose-500 ring-rose-200 dark:ring-rose-800';
    default:                  return 'bg-slate-400 ring-slate-200 dark:ring-slate-600';
  }
};

export const ApprovalTimeline = ({ history, className }: ApprovalTimelineProps) => {
  if (history.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12 text-slate-400', className)}>
        <Clock size={32} className="mb-2 opacity-30" />
        <p className="text-sm">No workflow actions recorded yet.</p>
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      {/* Vertical line */}
      <div className="absolute left-[19px] top-5 bottom-5 w-px bg-slate-200 dark:bg-slate-700" />

      <ol className="space-y-4">
        {history.map((h) => (
          <li key={h.id} className="flex items-start gap-4 relative">
            {/* Dot */}
            <div className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10',
              'ring-4',
              'bg-white dark:bg-slate-800',
              dotColor(h.toStatus),
            )}>
              {actionIcon(h.action, h.toStatus)}
            </div>

            {/* Card */}
            <div className="flex-1 pb-2">
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700
                              shadow-sm px-4 py-3">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-slate-800 dark:text-white">
                      {h.action}
                    </span>
                    <span className="text-[10px] font-medium text-slate-400
                                     bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">
                      {h.fromStatus} → {h.toStatus}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400 tabular-nums">
                    {new Date(h.actionDate).toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  by <span className="font-medium text-slate-700 dark:text-slate-300">{h.actionByName}</span>
                </p>
                {h.remarks && (
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-2
                                border-t border-slate-100 dark:border-slate-700 pt-2 italic">
                    "{h.remarks}"
                  </p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};
